import React from 'react';
import { 
    BottomNavigation, 
    BottomNavigationAction, 
    Paper,
    useTheme,
    alpha 
} from '@mui/material';
import {
    AutoAwesome,
    DescriptionOutlined,
    ExtensionOutlined,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUnifiedAccountState } from '../../hooks/useUnifiedAccountState';

interface MenuItem {
    text: string;
    icon: JSX.Element;
    path: string;
}

interface MobileBottomNavigationProps {
    isDisabled: boolean;
}

const getMenuItems = (t: (key: string) => string): MenuItem[] => [
    { text: t('flow.steps.dataImport'), icon: <ExtensionOutlined />, path: '/integrations' },
    { text: t('flow.steps.dataCheck'), icon: <AutoAwesome />, path: '/smart-check' },
    { text: t('flow.steps.taxReport'), icon: <DescriptionOutlined />, path: '/tax-reports' },
];

const MobileBottomNavigation: React.FC<MobileBottomNavigationProps> = ({ isDisabled }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();
    const location = useLocation();
    const menuItems = getMenuItems(t);
    
    // Check if integrations exist
    const accountState = useUnifiedAccountState({ skipIssueFetch: true });
    const hasIntegrations = accountState.hasIntegrations;

    const currentIndex = menuItems.findIndex(item => item.path === location.pathname);
    const value = currentIndex >= 0 ? currentIndex : false;

    const handleChange = (_event: React.SyntheticEvent, newValue: number) => {
        const item = menuItems[newValue];
        if (item) {
            // Disable smart-check and tax-reports if no integrations exist
            const isItemDisabled = !hasIntegrations && (item.path === '/smart-check' || item.path === '/tax-reports');
            if (!isItemDisabled) {
                navigate(item.path);
            }
        }
    };

    const bottomNavigation = (
        <Paper 
            sx={{ 
                position: 'fixed', 
                bottom: 0, 
                left: 0, 
                right: 0, 
                zIndex: 1400,
                borderTop: 1,
                borderColor: 'divider',
                backdropFilter: 'blur(8px)',
            }} 
            elevation={8}
        >
            <BottomNavigation
                value={value}
                onChange={handleChange}
                sx={{
                    bgcolor: 'transparent',
                }}
            >
                {menuItems.map((item, index) => {
                    // Disable smart-check and tax-reports if no integrations exist
                    const isItemDisabled = !hasIntegrations && (item.path === '/smart-check' || item.path === '/tax-reports');
                    
                    return (
                        <BottomNavigationAction
                            key={item.path}
                            label={item.text}
                            icon={item.icon}
                            disabled={isItemDisabled}
                        />
                    );
                })}
            </BottomNavigation>
        </Paper>
    );

    return bottomNavigation;
};

export default MobileBottomNavigation;